import { useState, useEffect, useCallback } from 'react';
import { keepAlive, sessionTimeout } from '../actions/session';
import { apiPaths } from '../utils/apiUtils';
import { setResetCallback } from '../api/api';

const useIdleSession = ({ warningPeriod = 60} = {}) => {
    const [warningOpen, setWarningOpen] = useState(false);
    const [warningTimeLeft, setWarningTimeLeft] = useState(warningPeriod); // 60-second warning period
    let lastActivity = Date.now();
    let idleSessionTime = 600;
    let logoutFlag = false; // Flag to check if the session logout is triggered

    const setIdleSessionTime = (time) => {
        idleSessionTime = time;
    }

    const setLogoutFlag = (value) => {
        logoutFlag = value;
    }
    // Fetch session timeout from the backend
    const fetchSessionTimeout = useCallback(async () => {
        try {
            const response = await sessionTimeout();
            setIdleSessionTime(response.timeoutInSeconds);
            return response.timeoutInSeconds;
        } catch (error) {
            console.error('Failed to fetch session timeout:', error);
            if (error.message === 'Session expired' || error.message === 'Session expired or invalid response') {
                handleSessionTimeout();
            }
            setIdleSessionTime(600);
            return 600;
        }
    }, []);

    // Extend session by calling /keep-alive
    const handleExtendSession = useCallback(async () => {
        try {
            const response = await keepAlive();
            setActivitytime();
            setWarningOpen(false);
        } catch (error) {
            console.error('Failed to extend session:', error);
            handleSessionTimeout();
        }
    }, []);

    // Handle session timeout (logout and redirect)
    const handleSessionTimeout = useCallback(() => {
        setLogoutFlag(true);
        setWarningOpen(false);
        sessionStorage.removeItem("user");
        window.location.assign(`${window.location.origin}${apiPaths.Oauth}/logout`);
    }, []);

    const setActivitytime = () => {
        lastActivity = Date.now();
    }

    // Monitor remaining time and show warning
    useEffect(() => {
        const interval = setInterval(() => {
            const remaining = idleSessionTime - Math.ceil(((Date.now()-lastActivity)/1000));
            // Show warning before timeout
            if (remaining <= warningPeriod && remaining > 0 && !logoutFlag) {
                setWarningOpen(true);
                setWarningTimeLeft(remaining);
            } else if(remaining <= 0 || logoutFlag) {
                clearInterval(interval); // Clear the interval to stop checking
                !logoutFlag && handleSessionTimeout(); // Trigger logout
                setLogoutFlag(false); // Reset the logout flag
            } else {
                setWarningOpen(false);
            }
        }, 1000); // Update every second
        return () => clearInterval(interval);
    }, []);

    // Initial fetch of session timeout
    useEffect(() => {
        fetchSessionTimeout();
    }, []);

    useEffect(() => {
        setResetCallback(setActivitytime);
        return () => {
            setResetCallback(() => {}); // Reset the callback when the component unmounts
        };
    }, []);

    return {
        idleSessionTime,
        warningOpen,
        warningTimeLeft,
        handleExtendSession,
        handleSessionTimeout,
        setActivitytime
    };
};

export default useIdleSession;